/**
 * Loads the dine-in menu (categories + items) from the live
 * dine-anonymous endpoint:
 *
 *   GET /api/restaurant/dine/{qrToken}/menu
 *     → {
 *         tenant:     { tenantId, name, code, logoUrl },
 *         table:      { tableId, code, name, tableStatus },
 *         categories: [{ menuCategoryId, code, name, description, sortOrder, imageUrl }],
 *         items:      [{ menuItemId, menuCategoryId, code, name, description,
 *                       price, imageUrl, isVeg, isAvailable, taxRate,
 *                       spiceLevel, preparationTimeMinutes }],
 *       }
 *
 * No auth header required — the qrToken IS the auth. This unblocks
 * the customer's menu screen for tenants that haven't provisioned
 * the `VITE_DINE_GUEST_USER` fallback (§32 / G39 closure).
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@kss/api';
import type { MenuCategory, MenuItem } from '@kss/api';

interface MenuState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  categories: MenuCategory[];
  items: MenuItem[];
  error: string | null;
}

/**
 * The dine endpoint returns image URLs as TENANT-RELATIVE paths
 * (`/api/Image/<sha>.png` or `/uploads/...`). On the customer dine page
 * the app origin is NOT the API origin (the API is cross-origin —
 * `https://api.ebillbook.kristysoftware.com/api`), so a plain
 * `<img src="/api/Image/…">` would resolve against the storefront host
 * and 404. Anchor relative paths to the API host so the browser fetches
 * the image from the right place. The `/api/Image/<sha>` route is now
 * anonymous (G61), so a plain <img> works once the URL is absolute.
 */
function absoluteImageUrl(value?: string): string {
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value; // already loadable
  if (!value.startsWith('/')) return value;                // unknown shape — leave it
  // baseURL is '/api' in dev (same origin → '') or 'https://host/api' in prod.
  const base = String(apiClient.defaults.baseURL ?? '').replace(/\/api\/?$/, '');
  return base + value;
}

interface DineMenuResponse {
  tenant?: { tenantId: string; name: string; code: string; logoUrl?: string | null };
  table?:  { tableId: number; code: string; name: string; tableStatus?: string };
  categories?: Array<{
    menuCategoryId: number;
    code: string;
    name: string;
    description?: string;
    sortOrder?: number;
    imageUrl?: string;
  }>;
  items?: Array<{
    menuItemId: number;
    menuCategoryId: number | null;
    code: string;
    name: string;
    description?: string;
    price: number;
    imageUrl?: string;
    isVeg?: boolean;
    isAvailable?: boolean;
    taxRate?: number;
    spiceLevel?: string;
    preparationTimeMinutes?: number;
  }>;
}

export function useDineMenu(qrToken?: string): MenuState {
  const [state, setState] = useState<MenuState>({
    status: 'idle',
    categories: [],
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!qrToken) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: null }));

    (async () => {
      try {
        const resp = await apiClient.get<DineMenuResponse>(
          `/restaurant/dine/${encodeURIComponent(qrToken)}/menu`,
        );
        if (cancelled) return;

        // Map the dine-endpoint shape onto the @kss/api MenuCategory + MenuItem
        // types the existing DineMenu component expects.
        const categories: MenuCategory[] = (resp.data?.categories ?? []).map((c) => ({
          menuCategoryId: String(c.menuCategoryId),
          code: c.code,
          name: c.name,
          description: c.description ?? '',
          sortOrder: c.sortOrder ?? 0,
          imageUrl: absoluteImageUrl(c.imageUrl),
          isActive: true,
        } as unknown as MenuCategory));

        const items: MenuItem[] = (resp.data?.items ?? [])
          .filter((i) => i.isAvailable !== false)
          .map((i) => ({
            menuItemId: String(i.menuItemId),
            menuCategoryId: i.menuCategoryId != null ? String(i.menuCategoryId) : undefined,
            code: i.code,
            name: i.name,
            description: i.description ?? '',
            price: i.price,
            imageUrl: absoluteImageUrl(i.imageUrl),
            isVeg: i.isVeg ?? false,
            isAvailable: i.isAvailable ?? true,
            taxRate: i.taxRate ?? 0,
            spiceLevel: i.spiceLevel ?? 'Mild',
            preparationTimeMinutes: i.preparationTimeMinutes ?? 0,
            isActive: true,
          } as unknown as MenuItem));

        // Sort categories by sortOrder asc, then by name.
        categories.sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
        );

        setState({ status: 'ready', categories, items, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: 'error',
          categories: [],
          items: [],
          error: (e as Error)?.message ?? 'Failed to load menu.',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [qrToken]);

  return state;
}
